import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { LifeBuoy, Plus, MessageSquare, Ticket, Clock, Trash2, ChevronLeft, ChevronRight } from "lucide-react"
import NewTicketForm from "./new-ticket-form"
import TicketDetails from "./ticket-details"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getUserTickets,
  deleteTicket,
  getTicketById,
  selectUserTickets,
  selectUserTicketsLoading,
  selectUserTicketsPagination,
  setUserTicketsPage,
  clearCurrentTicket,
} from "@/store/slices/ticketSlice"
import Loader from "@/components/loader/loader"

export default function Support() {
  const dispatch = useAppDispatch()
  const tickets = useAppSelector(selectUserTickets)
  const loading = useAppSelector(selectUserTicketsLoading)
  const pagination = useAppSelector(selectUserTicketsPagination)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [isTicketDetailsOpen, setIsTicketDetailsOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ticketToDelete, setTicketToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    dispatch(getUserTickets({ page: 1, limit: 10 }))
    return () => {
      dispatch(clearCurrentTicket())
    }
  }, [dispatch])

  const handleNewTicketSubmit = () => {
    // Refresh tickets list after creating a new ticket
    dispatch(getUserTickets({ page: 1, limit: 10 }))
  }

  const handleTicketReply = () => {
    // Refresh tickets list and reload selected ticket details
    dispatch(getUserTickets({ page: pagination.page, limit: 10 }))
    if (selectedTicket) {
      // Reload ticket details
      handleTicketDetailsReload(selectedTicket.ticket_id)
    }
  }

  const handleTicketDetailsReload = async (ticketId) => {
    try {
      const result = await dispatch(getTicketById(ticketId)).unwrap()
      setSelectedTicket({
        ...result.ticket,
        id: result.ticket.ticket_id,
        messages: result.messages,
      })
    } catch (error) {
      console.error("Failed to reload ticket:", error)
    }
  }

  const handleDeleteClick = (ticketId) => {
    setTicketToDelete(ticketId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!ticketToDelete) return

    setIsDeleting(true)
    try {
      await dispatch(deleteTicket(ticketToDelete)).unwrap()
      // Refresh tickets list (handled by Redux)
      dispatch(getUserTickets({ page: pagination.page, limit: 10 }))
      // Close details if the deleted ticket was open
      if (selectedTicket?.ticket_id === ticketToDelete) {
        closeTicketDetails()
      }
      setDeleteDialogOpen(false)
      setTicketToDelete(null)
    } catch (error) {
      console.error("Failed to delete ticket:", error)
    } finally {
      setIsDeleting(false)
    }
  }

  const openTicketDetails = async (ticket) => {
    setIsTicketDetailsOpen(true)
    try {
      const result = await dispatch(getTicketById(ticket.ticket_id)).unwrap()
      setSelectedTicket({
        ...result.ticket,
        id: result.ticket.ticket_id,
        messages: result.messages,
      })
    } catch (error) {
      console.error("Failed to fetch ticket details:", error)
      // Fallback to basic ticket info
      setSelectedTicket({
        ...ticket,
        id: ticket.ticket_id,
        messages: [],
      })
    }
  }

  const closeTicketDetails = () => {
    setSelectedTicket(null)
    setIsTicketDetailsOpen(false)
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-green-100 text-green-700"
      case "In Progress":
        return "bg-blue-100 text-blue-700"
      case "Pending":
        return "bg-yellow-100 text-yellow-700"
      case "Closed":
        return "bg-gray-100 text-gray-700"
      case "Resolved":
        return "bg-blue-100 text-blue-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const formatRelativeTime = (isoString) => {
    const date = new Date(isoString)
    const now = new Date()
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffSeconds < 60) return `${diffSeconds} seconds ago`
    const diffMinutes = Math.floor(diffSeconds / 60)
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`
    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) return `${diffHours} hours ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 30) return `${diffDays} days ago`
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <LifeBuoy className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-primary">Support Center</h1>
      </div>
      <p className="text-gray-600">Submit new support tickets or view your existing requests.</p>

      <Tabs defaultValue="my-tickets" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 text-primary">
          <TabsTrigger value="my-tickets" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <MessageSquare className="w-4 h-4 mr-2" /> My Tickets
          </TabsTrigger>
          <TabsTrigger value="new-ticket" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Plus className="w-4 h-4 mr-2" /> Submit New Ticket
          </TabsTrigger>
        </TabsList>

        {/* My Tickets Tab */}
        <TabsContent value="my-tickets" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Your Support Tickets</CardTitle>
              <CardDescription>A list of all your submitted support requests.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <Loader />
                </div>
              ) : tickets.length > 0 ? (
                <>
                  {tickets.map((ticket, index) => (
                    <div key={ticket.ticket_id}>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3">
                        <div className="flex-1 space-y-1 sm:space-y-0">
                          <h3 className="font-semibold text-primary text-lg">{ticket.subject}</h3>
                          <p className="text-sm text-gray-600">Category: {ticket.category}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>Last updated: {formatRelativeTime(ticket.updated_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-3 sm:mt-0">
                          <Badge className={getStatusBadgeColor(ticket.status)}>{ticket.status}</Badge>
                          <Dialog
                            open={isTicketDetailsOpen && selectedTicket?.ticket_id === ticket.ticket_id}
                            onOpenChange={setIsTicketDetailsOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                className="border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
                                onClick={() => openTicketDetails(ticket)}
                              >
                                View Details
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col">
                              <DialogHeader>
                                <DialogTitle>Ticket Details</DialogTitle>
                              </DialogHeader>
                              {selectedTicket && (
                                <TicketDetails
                                  ticket={selectedTicket}
                                  onReply={handleTicketReply}
                                  onClose={closeTicketDetails}
                                  onDelete={() => handleDeleteClick(selectedTicket.ticket_id)}
                                />
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteClick(ticket.ticket_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {index < tickets.length - 1 && <Separator />}
                    </div>
                  ))}
                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} total tickets)
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            dispatch(setUserTicketsPage(pagination.page - 1))
                            dispatch(getUserTickets({ page: pagination.page - 1, limit: 10 }))
                          }}
                          disabled={pagination.page <= 1}
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            dispatch(setUserTicketsPage(pagination.page + 1))
                            dispatch(getUserTickets({ page: pagination.page + 1, limit: 10 }))
                          }}
                          disabled={!pagination.hasMore}
                        >
                          Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg mb-4">You haven't submitted any tickets yet.</p>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-white"
                    onClick={() => document.querySelector('[value="new-ticket"]').click()}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Submit Your First Ticket
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submit New Ticket Tab */}
        <TabsContent value="new-ticket" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Submit a New Request</CardTitle>
              <CardDescription>Fill out the form below to open a new support ticket.</CardDescription>
            </CardHeader>
            <CardContent>
              <NewTicketForm
                onSubmit={handleNewTicketSubmit}
                onCancel={() => document.querySelector('[value="my-tickets"]').click()}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? "Deleting..." : "Delete Ticket"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
